/**
 * Severity presentation for the Dependency-Track mirror.
 *
 * DT's severity vocabulary is mapped to the chip tones of the function-colour
 * scale (Kritisch/Hoch → Alarm Rot, Mittel → Signalgelb, Niedrig → Flieder,
 * Info → neutral). UNASSIGNED is deliberately NOT rendered as harmless: it means DT has no
 * CVSS vector, which is common for the OT advisories that matter most here.
 */

import { msg } from '@lingui/core/macro';

import type { ChipTone } from '../../components/Chip';
import { translatedRecord } from '../../lib/locale';
import type { DtSeverity, Tone } from '../../lib/types';

/** Severity labels, translated on every read. */
export const SEVERITY_LABELS: Readonly<Record<DtSeverity, string>> = translatedRecord<DtSeverity>({
  CRITICAL: msg`Kritisch`,
  HIGH: msg`Hoch`,
  MEDIUM: msg`Mittel`,
  LOW: msg`Niedrig`,
  INFO: msg`Info`,
  UNASSIGNED: msg`Ohne Bewertung`,
});

export const SEVERITY_TONE: Readonly<Record<DtSeverity, ChipTone>> = {
  CRITICAL: 'alert',
  HIGH: 'alert',
  MEDIUM: 'warn',
  LOW: 'info',
  INFO: 'neutral',
  UNASSIGNED: 'warn',
};

/** Rank for sorting; unknown values sort last. */
export function severityRank(value: string | null | undefined): number {
  const order: readonly string[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO', 'UNASSIGNED'];
  const index = order.indexOf(String(value ?? '').toUpperCase());
  return index < 0 ? order.length : index;
}

/** Label for the four project states, translated on every read. */
export const SCAN_STATE_LABELS: Readonly<Record<string, string>> = translatedRecord({
  ok: msg`Gescannt`,
  ausstehend: msg`Scan läuft`,
  'kein-projekt': msg`Kein SBOM`,
  unbekannt: msg`Unbekannt`,
});

export const SCAN_STATE_TONE: Readonly<Record<string, Tone>> = {
  ok: 'good',
  ausstehend: 'warn',
  'kein-projekt': 'alert',
  unbekannt: 'neutral',
};

/** EPSS as a percentage with one decimal, or the dash. */
export function fmtEpss(value: number | null | undefined): string | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return `${(value * 100).toFixed(1)} %`;
}
